import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface ChipProps {
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Группа с одиночным выбором: озвучивается как переключатель, а не как чекбокс */
  single?: boolean;
  /** Небольшая подпись справа (например, число людей в наборе) */
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Чип для мультивыбора (районы, вечера, активности). */
export function Chip({ selected, disabled, onClick, single, meta, children, className }: ChipProps) {
  return (
    <button
      type="button"
      role={onClick ? (single ? 'radio' : 'checkbox') : undefined}
      aria-checked={onClick ? Boolean(selected) : undefined}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium',
        'pressable press-scale',
        'select-none disabled:opacity-50',
        selected
          ? 'border-accent bg-accent/15 text-accent'
          : 'border-border bg-surface text-fg active:bg-surface-2',
        className,
      )}
    >
      <span>{children}</span>
      {/* Раунд 4, N8: без разделителя имя кнопки читалось «Вечер19:00–22:00» */}
      {meta ? <span className="sr-only">, </span> : null}
      {meta ? (
        <span className={cn('text-xs', selected ? 'text-accent/80' : 'text-muted')}>{meta}</span>
      ) : null}
    </button>
  );
}

export interface ChipGroupProps {
  children: ReactNode;
  className?: string;
  /** Для чипов `single`: группа озвучивается как выбор одного варианта */
  radio?: boolean;
  label?: string;
}

export function ChipGroup({ children, className, radio, label }: ChipGroupProps) {
  return (
    <div
      className={cn('flex flex-wrap gap-2', className)}
      role={radio ? 'radiogroup' : undefined}
      aria-label={label}
    >
      {children}
    </div>
  );
}
